import {Component, computed, ElementRef, inject, input, model, OnInit, output, signal, viewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MultiSelect} from 'primeng/multiselect';
import {Button} from 'primeng/button';
import {DatePicker} from 'primeng/datepicker';
import {InputText} from 'primeng/inputtext';
import {Chip} from 'primeng/chip';
import {FilterService} from 'primeng/api';
import {BasicUserDetails, LocationOption, ProjectDetails, SearchTaType, TAStatus, UserType} from '../../../../api';
import {AdvancedSearchParameters, toTAStatusText} from '../../../../util/app.types';
import {TaAppStore} from '../../../../state/clcta-store';
import {PermissionService} from '../../../../services/permission.service';
import {TaSearchDatasource} from '../../../../datasources/ta-search-datasource';
import {getUserTypeSuffix} from '../../../../util/general.utils';
import {fuzzySearch} from '../../../../util/search.utils';

/** Basic/advanced TA search form. Stateless: the parent owns the term, parameters and mode. */
@Component({
  selector: 'submission-search-bar',
  imports: [FormsModule, MultiSelect, Button, DatePicker, InputText, Chip],
  // Own FilterService so concurrently-alive bars don't overwrite each other's fuzzy rules.
  providers: [FilterService],
  templateUrl: './submission-search-bar.component.html',
  host: {class: 'block'},
})
export class SubmissionSearchBarComponent implements OnInit {
  private store = inject(TaAppStore);
  private permissionService = inject(PermissionService);
  private taSearchDatasource = inject(TaSearchDatasource);
  private filterService = inject(FilterService);
  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  advancedEnabled = model(false);
  basicSearchStr = model('');
  // Bound field-by-field via ngModel, so edits mutate the parent's object.
  advancedSearchParameters = input.required<AdvancedSearchParameters>();
  loading = input(false);
  bordered = input(false);
  search = output<void>();
  clear = output<void>();

  // Custom FilterService rules. PrimeNG types filterMatchMode as its built-ins only,
  // but MultiSelect looks any registered rule up by name at runtime, hence the cast.
  protected readonly fuzzyMatchMode = {
    staff: 'fuzzyStaff',
    approver: 'fuzzyApprover',
    creator: 'fuzzyCreator',
    location: 'fuzzyLocation',
  } as unknown as Record<'staff' | 'approver' | 'creator' | 'location', MultiSelect['filterMatchMode']>;

  constructor() {
    // The multiselects filter through these modes so their search uses the ranked fuzzy matches.
    this.filterService.register(this.fuzzyMatchMode.staff, (value: string) => this.staffMatch().names.has(value));
    this.filterService.register(this.fuzzyMatchMode.approver, (value: string) => this.approverMatch().names.has(value));
    this.filterService.register(this.fuzzyMatchMode.creator, (value: string) => this.creatorMatch().names.has(value));
    this.filterService.register(this.fuzzyMatchMode.location, (value: string) => this.locationMatch().names.has(value));
  }

  availableProjects = computed<ProjectDetails[]>(() => {

    if (this.permissionService.hasGlobalSearch()) {
      return this.store.availableProjects();
    }
    const managedUnitIds = new Set(
      (this.permissionService.currentUser()?.managedUnits ?? []).map(u => u.id).filter((id): id is string => !!id));
    return this.store.availableProjects().filter(p => !!p.unitId && managedUnitIds.has(p.unitId));
  });
  availableUsers = computed<BasicUserDetails[]>(() => this.store.userSearchOptions());
  protected readonly getUserTypeSuffix = getUserTypeSuffix;
  availableLocations = computed<LocationOption[]>(() => this.store.availableLocations());

  // Multiselect filter text, kept in sync via (onFilter).
  staffQuery = signal('');
  approverQuery = signal('');
  creatorQuery = signal('');
  locationQuery = signal('');
  staffMatch = computed(() => this.rankOptions(this.availableUsers(), this.staffQuery()));
  approverMatch = computed(() => this.rankOptions(this.availableUsers(), this.approverQuery()));
  creatorMatch = computed(() => this.rankOptions(this.availableUsers(), this.creatorQuery()));
  locationMatch = computed(() => this.rankOptions(this.availableLocations(), this.locationQuery(), 10));
  statusOptions = Object.values(TAStatus)
    .filter(status => status !== TAStatus.Deleted)
    .map(status => ({
      label: toTAStatusText(status),
      value: status
    }));

  taTypeOptions: { label: string, value: SearchTaType }[] = [
    {label: 'Prepaid', value: SearchTaType.PrePaid},
    {label: 'Day Trip', value: SearchTaType.DayTrip},
    {label: 'Flight Booking', value: SearchTaType.FlightBooking},
  ];

  // Retired (disabled) purposes stay selectable so historic TAs remain searchable
  purposeOptions = computed<{ label: string, value: string }[]>(() =>
    (this.store.session().details?.travelReasonOptions ?? [])
      .map(option => ({label: option.label ?? option.value ?? '', value: option.value ?? ''})));

  ngOnInit(): void {
    this.taSearchDatasource.loadProjectOptions();
    this.taSearchDatasource.loadUserOptionsForSearch();
    this.taSearchDatasource.loadLocationOptions();
  }

  // No-op in advanced mode (no keyword input rendered).
  focusInput(): void {
    const input = this.searchInput()?.nativeElement;
    input?.focus();
    input?.select();
  }

  toggleMode() {
    this.advancedEnabled.set(!this.advancedEnabled());
  }

  /**
   * Matches first in ranked order, then the rest. The full list stays in [options] because the
   * multiselect resolves selected labels and select-all from it; `names` drives the filter mode.
   * `limit` caps the visible matches while a query is entered.
   */
  private rankOptions<T extends { name?: string | null }>(options: T[], query: string, limit?: number) {
    const ranked = fuzzySearch(options, query, 'name');
    const matches = limit && query.trim() ? ranked.slice(0, limit) : ranked;
    const matched = new Set(matches);
    return {
      options: [...matches, ...options.filter(option => !matched.has(option))],
      names: new Set(matches.map(match => match.name)),
    };
  }

  protected readonly UserType = UserType;
}
